/** biome-ignore-all lint/correctness/useHookAtTopLevel: look at later */
import { apiClient } from "@ruter-as/web-components-and-tools";
import * as Sentry from "@sentry/react";
import React, {
	type PropsWithChildren,
	useCallback,
	useContext,
	useEffect,
	useState,
} from "react";
import { useLocation, useNavigate } from "react-router";
import type { Subscription } from "../../api/subscription";
import type { Ticket } from "../../api/Ticket";
import widgetApi from "../../api/widgetApi";
import type {
	Company,
	CompanyWithAvailableSubscriptions,
} from "../../types/Company";
import type { ProductTemplate } from "../../types/ProductTemplate";
import type { Zone } from "../../zones";
import InitError from "../error/InitError";
import Loading from "../loading/Loading";
import LoginForm from "../login/LoginForm";
import SelectCompanyForm from "../login/SelectCompanyForm";
import PreviewComponent from "../preview/Preview";
import SubscriptionInfoPage from "../wrapper/SubscriptionInfo";
import WidgetWrapper from "../wrapper/WidgetWrapper";
import { getAppInitParameters, saveInitParamters } from "./appInitParameters";
import type { UserData } from "./userData";

interface AppStateInit {
	type: "app-state-init";
}

interface AppStateError {
	type: "app-state-error";
	errorType:
		| "ERROR_REASON_MISSING_CLIENTID"
		| "ERROR_REASON_MISSING_SECRET"
		| "ERROR_REASON_SERVER_ERROR"
		| "ERROR_REASON_INCOMPLETE_USBL_PARAMS";
	error: Error;
	missingUsblParams?: string[];
}

interface AppStatePreview {
	type: "app-state-preview";
	zones: Zone[];
	companies: CompanyWithAvailableSubscriptions[];
}

interface AppStateNeedUserInput {
	type: "app-state-need-user-input";
	zones: Zone[];
	companies: CompanyWithAvailableSubscriptions[];
	token: string;
	viewMode: "widget" | "link";
	id: string;
	secret: string;
}

interface AppStateComplete {
	type: "app-state-complete";
	zones: Zone[];
	token: string;
	company: CompanyWithAvailableSubscriptions;
	subscriptions: Subscription[];
	tickets: Ticket[];
	viewMode: "widget" | "link";
	userData: UserData;
	id: string;
	secret: string;
	productTemplate: ProductTemplate;
}

interface AppStateNeedCompanySelection {
	type: "app-state-need-company-selection";
	zones: Zone[];
	companies: CompanyWithAvailableSubscriptions[];
	token: string;
	viewMode: "widget" | "link";
	id: string;
	secret: string;
	userData: UserData;
	subscriptions: Subscription[];
	tickets: Ticket[];
}

type AppState =
	| AppStateInit
	| AppStateError
	| AppStatePreview
	| AppStateNeedUserInput
	| AppStateNeedCompanySelection
	| AppStateComplete;

interface AuthenticatedContextValues {
	authenticated: true;
	token: string;
	selectedCompany: CompanyWithAvailableSubscriptions;
	userData: UserData;
	tickets: Ticket[];
	subscriptions: Subscription[];
	refetchSubscriptions: () => Promise<void>;
	viewMode: "link" | "widget";
	zones: Zone[];
	productTemplate: ProductTemplate;
}

interface NonAuthenticatedContextValues {
	authenticated: false;
	zones: Zone[];
	selectedCompany: Company;
}

type AuthContextValues =
	| NonAuthenticatedContextValues
	| AuthenticatedContextValues;

const TRAVEL_SUBSCRIPTION_TOKEN = "6aa5d845-ee37-4fa3-8d04-c67854d3fa67";

const AuthContext = React.createContext<AuthContextValues | null>(null);

const AuthContextProvider: React.FC<PropsWithChildren<unknown>> = ({
	children,
}) => {
	const [appState, setAppState] = useState<AppState>({
		type: "app-state-init",
	});

	const location = useLocation();
	const navigate = useNavigate();

	if (location.pathname.indexOf("public/info") !== -1) {
		return <SubscriptionInfoPage />;
	}

	const fetchAppData = useCallback(async (id: string, secret: string) => {
		const [tokenAndCompanyResponse, zonesResponse] = await Promise.all([
			apiClient.request(widgetApi.auth.getToken(id, secret)),
			apiClient.request(widgetApi.zones.getAll()),
		]);

		if (tokenAndCompanyResponse.error) {
			return { type: "error" as const, error: tokenAndCompanyResponse.error };
		}
		if (zonesResponse.error) {
			return { type: "error" as const, error: zonesResponse.error };
		}

		const { companies, token } = tokenAndCompanyResponse.result;

		const responses = await Promise.all(
			companies.map((company) =>
				apiClient.request(
					widgetApi.subscriptions.getAvailable(token, company.id),
				),
			),
		);

		const error = responses.find((x) => x.type !== "success");

		if (error) {
			return { type: "error" as const, error: error.error };
		}

		const companiesWithAvailableSubscriptions: CompanyWithAvailableSubscriptions[] =
			companies.map((company, index) => {
				const availableSubscriptions =
					responses[index].type === "success" ? responses[index].result : [];
				return {
					...company,
					availableSubscriptions: availableSubscriptions.filter(
						(x) => x.type !== "UNKNOWN",
					),
					hasTravelSubscription: availableSubscriptions.some(
						(x) => x.type === "TRAVEL_SUBSCRIPTION",
					),
				};
			});

		return {
			type: "success" as const,
			zones: zonesResponse.result,
			companies: companiesWithAvailableSubscriptions,
			token: tokenAndCompanyResponse.result.token,
		};
	}, []);

	const handleInitError = useCallback((error: Error) => {
		setAppState({
			type: "app-state-error",
			errorType: "ERROR_REASON_SERVER_ERROR",
			error,
		});
		Sentry.captureException(error);
	}, []);

	const finishAuthProcess = useCallback(
		async (
			id: string,
			secret: string,
			token: string,
			userData: UserData,
			company: CompanyWithAvailableSubscriptions,
			tickets: Ticket[],
			subscriptions: Subscription[],
			zones: Zone[],
			viewMode: "widget" | "link",
		): Promise<boolean> => {
			const productResponse = await apiClient.request(
				widgetApi.products.getAll(),
			);

			if (productResponse.error) {
				handleInitError(productResponse.error);
				return false;
			}

			const hasTravelSubscription = company.availableSubscriptions.some(
				(x) => x.type === "TRAVEL_SUBSCRIPTION",
			);

			const productTemplate = productResponse.result.find((x) =>
				hasTravelSubscription
					? x.id === TRAVEL_SUBSCRIPTION_TOKEN
					: x.tags.includes("bigcustomer:subscriptionTicket"),
			);

			if (!productTemplate) {
				handleInitError(new Error("could not find productTemplate"));
				return false;
			}

			setAppState({
				type: "app-state-complete",
				company,
				subscriptions: hasTravelSubscription ? [] : subscriptions,
				tickets,
				token,
				zones,
				viewMode,
				userData,
				id,
				secret,
				productTemplate,
			});
			saveInitParamters({
				type: "init-result-user-authenticated",
				id,
				secret,
				selectedCompanyId: company.id,
				token,
				userData,
				viewMode,
			});
			return true;
		},
		[handleInitError],
	);

	useEffect(() => {
		const init = async () => {
			const appInitParameters = getAppInitParameters();

			switch (appInitParameters.type) {
				case "init-result-error": {
					setAppState({
						type: "app-state-error",
						error: appInitParameters.error,
						errorType: appInitParameters.errorType,
						missingUsblParams: appInitParameters.missingUsblParams,
					});
					Sentry.captureException(appInitParameters.error);
					break;
				}
				case "init-result-parameters-ok": {
					const appData = await fetchAppData(
						appInitParameters.id,
						appInitParameters.secret,
					);
					if (appData.type === "error") {
						handleInitError(appData.error);
						return;
					}

					if (appInitParameters.viewMode === "preview") {
						setAppState({
							...appData,
							type: "app-state-preview",
						});
					} else {
						setAppState({
							...appData,
							type: "app-state-need-user-input",
							viewMode: appInitParameters.viewMode,
							id: appInitParameters.id,
							secret: appInitParameters.secret,
						});
					}
					break;
				}
				case "init-result-user-authenticated": {
					const { id, secret, token, selectedCompanyId, userData, viewMode } =
						appInitParameters;
					const appData = await fetchAppData(id, secret);
					const [subscriptionsResponse, ticketsResponse] = await Promise.all([
						apiClient.request(
							widgetApi.subscriptions.getForUser(token, userData),
						),
						apiClient.request(widgetApi.tickets.getAll(token, userData)),
					]);

					if (appData.error) {
						handleInitError(appData.error);
						return;
					}
					if (subscriptionsResponse.error) {
						handleInitError(subscriptionsResponse.error);
						return;
					}
					if (ticketsResponse.error) {
						handleInitError(ticketsResponse.error);
						return;
					}

					const { zones, companies } = appData;
					const subscriptions = subscriptionsResponse.result.matches;
					const tickets = ticketsResponse.result.matches;

					const company = companies.find((x) => x.id === selectedCompanyId);

					if (!company) {
						handleInitError(
							new Error(
								`could not find company with id=${selectedCompanyId} in list`,
							),
						);
						return;
					}

					finishAuthProcess(
						id,
						secret,
						token,
						userData,
						company,
						tickets,
						subscriptions,
						zones,
						viewMode,
					);
					break;
				}
				case "init-result-usbl": {
					const { id, secret, userData, viewMode } = appInitParameters;
					const appData = await fetchAppData(id, secret);

					if (appData.type === "error") {
						handleInitError(appData.error);
						return;
					}

					const { token, zones, companies } = appData;

					const verifyResponse = await apiClient.request(
						widgetApi.auth.verifyPhoneCode(token, userData),
					);

					if (verifyResponse.type !== "success") {
						handleInitError(verifyResponse.error);
						return;
					}

					const [subscriptionsResponse, ticketsResponse] = await Promise.all([
						apiClient.request(
							widgetApi.subscriptions.getForUser(token, userData),
						),
						apiClient.request(widgetApi.tickets.getAll(token, userData)),
					]);

					if (subscriptionsResponse.error) {
						handleInitError(subscriptionsResponse.error);
						return;
					}
					if (ticketsResponse.error) {
						handleInitError(ticketsResponse.error);
						return;
					}

					const subscriptions = subscriptionsResponse.result.matches;
					const tickets = ticketsResponse.result.matches;

					if (companies.length === 1) {
						const ok = await finishAuthProcess(
							id,
							secret,
							token,
							userData,
							companies[0],
							tickets,
							subscriptions,
							zones,
							viewMode,
						);
						if (ok) {
							if (subscriptions.length === 0 && tickets.length === 0) {
								navigate("/abonnement/ny");
							} else {
								navigate("/abonnement");
							}
						}
					} else {
						setAppState({
							type: "app-state-need-company-selection",
							zones,
							companies,
							token,
							viewMode,
							id,
							secret,
							userData,
							subscriptions,
							tickets,
						});
					}
					break;
				}
				default: {
					const exhaustiveCheck: never = appInitParameters;
					throw new Error(`Unhandled switch case: ${exhaustiveCheck}`);
				}
			}
		};
		init();
	}, [fetchAppData, finishAuthProcess, handleInitError, navigate]);

	switch (appState.type) {
		case "app-state-error": {
			console.error(appState.error);
			return (
				<WidgetWrapper>
					<InitError
						error={appState.errorType}
						missingUsblParams={appState.missingUsblParams}
					/>
				</WidgetWrapper>
			);
		}
		case "app-state-init": {
			return (
				<WidgetWrapper>
					<Loading />
				</WidgetWrapper>
			);
		}
		case "app-state-preview": {
			return (
				<AuthContext
					value={{
						authenticated: false,
						zones: appState.zones,
						selectedCompany: appState.companies[0],
					}}
				>
					<WidgetWrapper>
						<PreviewComponent />
					</WidgetWrapper>
				</AuthContext>
			);
		}
		case "app-state-need-user-input": {
			return (
				<WidgetWrapper>
					<LoginForm
						token={appState.token}
						companies={appState.companies}
						handleInitError={handleInitError}
						onNeedCompanySelection={(userData, subscriptions, tickets) => {
							setAppState({
								type: "app-state-need-company-selection",
								zones: appState.zones,
								companies: appState.companies,
								token: appState.token,
								viewMode: appState.viewMode,
								id: appState.id,
								secret: appState.secret,
								userData,
								subscriptions,
								tickets,
							});
						}}
						onLoginComplete={async (
							userData: UserData,
							company: CompanyWithAvailableSubscriptions,
							subscriptions: Subscription[],
							tickets: Ticket[],
						) => {
							const ok = await finishAuthProcess(
								appState.id,
								appState.secret,
								appState.token,
								userData,
								company,
								tickets,
								subscriptions,
								appState.zones,
								appState.viewMode,
							);
							if (ok) {
								if (subscriptions.length === 0 && tickets.length === 0) {
									navigate("/abonnement/ny");
								} else {
									navigate("/abonnement");
								}
							}
						}}
					/>
				</WidgetWrapper>
			);
		}
		case "app-state-need-company-selection": {
			return (
				<WidgetWrapper>
					<SelectCompanyForm
						companies={appState.companies}
						onSubmit={async (company) => {
							const ok = await finishAuthProcess(
								appState.id,
								appState.secret,
								appState.token,
								appState.userData,
								company,
								appState.tickets,
								appState.subscriptions,
								appState.zones,
								appState.viewMode,
							);
							if (ok) {
								if (
									appState.subscriptions.length === 0 &&
									appState.tickets.length === 0
								) {
									navigate("/abonnement/ny");
								} else {
									navigate("/abonnement");
								}
							}
						}}
					/>
				</WidgetWrapper>
			);
		}
		case "app-state-complete": {
			return (
				<AuthContext.Provider
					value={{
						authenticated: true,
						token: appState.token,
						selectedCompany: appState.company,
						userData: appState.userData,
						viewMode: appState.viewMode,
						tickets: appState.tickets,
						subscriptions: appState.subscriptions,
						zones: appState.zones,
						productTemplate: appState.productTemplate,
						refetchSubscriptions: async () => {
							const [subscriptionsResponse, ticketsResponse] =
								await Promise.all([
									apiClient.request(
										widgetApi.subscriptions.getForUser(
											appState.token,
											appState.userData,
										),
									),
									apiClient.request(
										widgetApi.tickets.getAll(appState.token, appState.userData),
									),
								]);

							if (subscriptionsResponse.error) {
								setAppState(() => {
									throw subscriptionsResponse.error;
								});
								return;
							}
							if (ticketsResponse.error) {
								setAppState(() => {
									throw ticketsResponse.error;
								});
								return;
							}

							setAppState({
								...appState,
								tickets: ticketsResponse.result.matches,
								subscriptions: subscriptionsResponse.result.matches,
							});
						},
					}}
				>
					{children}
				</AuthContext.Provider>
			);
		}
		default: {
			const exhaustiveCheck: never = appState;
			throw new Error(`Unhandled switch case: ${exhaustiveCheck}`);
		}
	}
};

export const useAuthContext = (): AuthContextValues => {
	const context = useContext(AuthContext);

	if (context === null) {
		throw new Error("context cannot be null");
	}

	return context;
};

export const useAuthenticatedAuthContext = (): AuthenticatedContextValues => {
	const authContext = useAuthContext();
	if (!authContext.authenticated) {
		throw new Error("expected user to be authenticated");
	}

	return authContext;
};

export default AuthContextProvider;
